const fmt = (n) => (n ?? 0).toLocaleString("en-IN");
const fmtPct = (n, digits = 1) => {
  const v = Number(n ?? 0);
  return `${v > 0 ? "+" : ""}${v.toFixed(digits)}%`;
};

export const buildSmiGaugeStyle = (smi = 0) => {
  if (smi == null) {
    return {
      background:
        "radial-gradient(circle at center, #fff 0 52%, transparent 53%), conic-gradient(rgba(37,99,235,.12) 0 100%)",
    };
  }
  const pct = Math.min(100, Math.max(0, Number(smi) || 0));
  return {
    background: `radial-gradient(circle at center, #fff 0 52%, transparent 53%), conic-gradient(#2563eb 0 ${pct}%, rgba(37,99,235,.12) ${pct}% 100%)`,
  };
};

export const buildSchoolMovementIndex = (schoolDistribution = []) => {
  const increased = schoolDistribution.find((d) => d.name === "Increased Staffing")?.value ?? 0;
  const decreased = schoolDistribution.find((d) => d.name === "Decreased Staffing")?.value ?? 0;
  const noChange = schoolDistribution.find((d) => d.name === "No Change")?.value ?? 0;
  const total = increased + decreased + noChange;
  if (!total) return null;
  return Math.round(((increased + noChange) / total) * 100);
};

export const buildDrilldownHeroHeadline = () => "School Drilldown Command Center";

export const buildDrilldownHeroNarrative = (topLosses = []) => {
  if (!topLosses.length) {
    return "School-level movement is stable overall, but high-loss schools should be verified before staffing gaps affect classroom delivery.";
  }
  return "School-level movement is stable overall, but the top loss list needs immediate verification before staffing gaps affect classroom delivery.";
};

export const buildDrilldownStatusChips = (topGains = [], topLosses = [], blockData = []) => [
  { label: `${topgains.length} High-Gain Schools`, className: "green" },
  { label: `${toplosses.length} High-Loss Schools`, className: "red" },
  { label: `${blockdata.length} Block Profiles`, className: "" },
];

export const buildDrilldownPrimaryAction = (topLosses = []) => {
  const blocks = [...new Set(topLosses.slice(0, 5).map((s) => s.block_name).filter(Boolean))].slice(0, 3);
  const names = blocks.map((b) => b?.toUpperCase()).join(", ");
  if (names) {
    return `Validate the top loss schools in ${names}, then trigger redeployment review where CY strength has dropped sharply.`;
  }
  return "Validate the top loss schools, then trigger redeployment review where CY strength has dropped sharply.";
};

export const buildDrilldownKpis = (overview = {}, topLosses = []) => {
  const growth = overview.teacher_growth ?? 0;
  const growthLabel = growth >= 0 ? `+${fmt(growth)}` : fmt(growth);
  return [
    {
      label: "Schools Monitored",
      value: fmt(overview.total_schools),
      sub: "Across selected scope",
      cardClass: "",
    },
    {
      label: "Total Teachers CY",
      value: fmt(overview.teachers_cy),
      sub: "Current year base",
      cardClass: "",
    },
    {
      label: "Net Staffing Change",
      value: growthLabel,
      sub: "District-wide movement",
      cardClass: "",
    },
    {
      label: "Critical Loss Schools",
      value: String(topLosses.length),
      sub: topLosses.length > 0 ? "Need field validation" : "No loss schools in scope",
      cardClass: topLosses.length > 0 ? "danger" : "",
    },
  ];
};

export const buildRankingList = (schools = [], type = "gain") =>
  schools.slice(0, 10).map((school, idx) => {
    const change = school.change ?? 0;
    const sign = change > 0 ? "+" : "";
    return {
      rank: idx + 1,
      schoolName: school.school_name || "—",
      udiseCode: school.udise_code,
      blockName: (school.block_name || "—").toUpperCase(),
      teachersPy: school.teachers_py ?? 0,
      teachersCy: school.teachers_cy ?? 0,
      changeLabel: `${sign}${change}`,
      badgeClass: type === "loss" ? "red" : "",
    };
  });

export const buildSpotlightCards = (blockData = []) => {
  const blocks = [...blockData].filter((b) => b.block_name);
  const byGrowth = [...blocks].sort((a, b) => (b.teacher_growth_pct ?? 0) - (a.teacher_growth_pct ?? 0))[0];
  const byRisk = [...blocks].sort((a, b) => (b.risk_index ?? 0) - (a.risk_index ?? 0))[0];
  const byTqi = [...blocks].sort((a, b) => (b.quality_index ?? 0) - (a.quality_index ?? 0))[0];
  const byTeachers = [...blocks].sort((a, b) => (b.teachers_cy ?? 0) - (a.teachers_cy ?? 0))[0];

  return [
    {
      id: "growth",
      title: "Highest Growth Block",
      block: byGrowth?.block_name || "—",
      metric: fmtPct(byGrowth?.teacher_growth_pct ?? 0),
      description: `Strong staffing expansion with ${fmt(byGrowth?.teachers_cy)} current-year teachers.`,
      iconClass: "green",
    },
    {
      id: "risk",
      title: "Highest TRI Risk",
      block: byRisk?.block_name || "—",
      metric: String(byRisk?.risk_index ?? 0),
      description: "Prioritize school-level deployment validation and teacher quality support.",
      iconClass: "red",
    },
    {
      id: "tqi",
      title: "Best TQI Block",
      block: byTqi?.block_name || "—",
      metric: Number(byTqi?.quality_index ?? 0).toFixed(1),
      description: "Strongest combined quality signal among listed blocks.",
      iconClass: "",
    },
    {
      id: "base",
      title: "Largest Teacher Base",
      block: byTeachers?.block_name || "—",
      metric: fmt(byTeachers?.teachers_cy),
      description: "High-scale block; small percentage changes affect many classrooms.",
      iconClass: "purple",
    },
  ];
};

const blockStatus = (block) => {
  const growth = block.teacher_growth_pct ?? 0;
  const risk = block.risk_index ?? 0;
  if (risk >= 7 || (growth < -2 && risk >= 6)) return { label: "Critical", className: "red" };
  if (growth < 0 || risk >= 5.5) return { label: "Risk", className: "red" };
  if (risk >= 4.5) return { label: "Watch", className: "orange" };
  if (growth >= 2 && risk < 4) return { label: "Good", className: "green" };
  return { label: "Stable", className: "" };
};

const triTagClass = (tri) => {
  if (tri >= 7) return "red";
  if (tri >= 4.5) return "orange";
  return "";
};

export const buildBlockMetricsTable = (blockData = []) =>
  [...blockData]
    .filter((b) => b.block_name)
    .sort((a, b) => (b.teachers_cy ?? 0) - (a.teachers_cy ?? 0))
    .slice(0, 14)
    .map((block) => {
      const growth = block.teacher_growth_pct ?? 0;
      const status = blockStatus(block);
      return {
        blockCode: block.block_code,
        blockName: block.block_name?.toUpperCase() || "—",
        schools: block.total_schools ?? 0,
        teachersCy: block.teachers_cy ?? 0,
        growthLabel: fmtPct(growth),
        growthClass: growth >= 0 ? "green" : "red",
        ctet: `${Number(block.ctet_pct ?? 0).toFixed(1)}%`,
        cwsn: `${Number(block.cwsn_pct ?? 0).toFixed(1)}%`,
        digital: `${Number(block.computer_pct ?? 0).toFixed(1)}%`,
        tqi: Number(block.quality_index ?? 0).toFixed(1),
        tri: Number(block.risk_index ?? 0).toFixed(1),
        triClass: triTagClass(block.risk_index ?? 0),
        status: status.label,
        statusClass: status.className,
      };
    });

export const buildPriorityIndicators = (topLosses = [], topGains = [], blockData = []) => {
  const lossSeverity = Math.min(95, 40 + topLosses.length * 3);
  const gainCount = topGains.length;
  const avgTqi =
    blockData.length > 0
      ? blockData.reduce((s, b) => s + (b.quality_index ?? 0), 0) / blockData.length
      : 30;
  const maxTri = Math.max(...blockData.map((b) => b.risk_index ?? 0), 0);

  return [
    { label: "Staffing Loss", width: lossSeverity, barClass: "red", level: lossSeverity >= 65 ? "High" : "Medium" },
    { label: "Growth Validation", width: Math.min(85, 45 + gainCount * 2), barClass: "green", level: "Medium" },
    { label: "TQI Gap", width: Math.min(80, Math.round(100 - avgTqi)), barClass: "orange", level: avgTqi < 30 ? "Open" : "Medium" },
    { label: "TRI Exposure", width: Math.min(95, Math.round(maxTri * 10)), barClass: "red", level: maxTri >= 6 ? "Critical" : "High" },
  ];
};

export const buildDrilldownActionQueue = (topLosses = []) => {
  const lossNames = topLosses
    .slice(0, 3)
    .map((s) => s.school_name)
    .filter(Boolean)
    .join(", ");

  return [
    {
      title: "Validate High-Loss Schools First",
      body: lossNames
        ? `Check ${lossNames} and other top loss schools for transfer, retirement or data-entry causes.`
        : "Check top loss schools for transfer, retirement or data-entry causes.",
      iconClass: "red",
    },
    {
      title: "Review High-Gain Schools for Capacity Impact",
      body: "Confirm whether large gains are due to new sections, school expansion or corrected previous-year reporting.",
      iconClass: "green",
    },
    {
      title: "Create Block Officer Follow-Up Pack",
      body: "Send a block-wise list with school, PY, CY and change to BEO/cluster teams before the next review.",
      iconClass: "orange",
    },
  ];
};

export const buildDrilldownReviewNote = (topLosses = [], topGains = []) => {
  const lossCount = topLosses.length;
  const gainCount = topGains.length;
  if (!lossCount && !gainCount) {
    return "No school movement outliers in the current scope. Continue weekly monitoring once staffing changes appear.";
  }
  return `Review ${lossCount} loss and ${gainCount} gain school${lossCount + gainCount === 1 ? "" : "s"} weekly, validate reasons, and track deployment gap closure.`;
};

export const filterSchools = (schools = [], { search = "", block = "All", movement = "Gain/Loss" } = {}) => {
  const q = search.trim().toLowerCase();
  return schools.filter((school) => {
    if (q) {
      const name = (school.school_name || "").toLowerCase();
      const udise = String(school.udise_code || "").toLowerCase();
      if (!name.includes(q) && !udise.includes(q)) return false;
    }
    if (block && block !== "All" && school.block_name !== block) return false;
    if (movement === "Gain" && (school.change ?? 0) <= 0) return false;
    if (movement === "Loss" && (school.change ?? 0) >= 0) return false;
    return true;
  });
};

export const uniqueBlocks = (blockData = []) => [
  "All",
  ...[...new Set(blockData.map((b) => b.block_name).filter(Boolean))].sort(),
];

export const buildTeacherDrilldownMarkdown = (topLosses = [], topGains = []) => {
  const actions = buildDrilldownActionQueue(topLosses);
  return [
    "## Insights",
    `- ${buildDrilldownHeroHeadline()}`,
    `- ${buildDrilldownHeroNarrative(topLosses)}`,
    ...topLosses.slice(0, 3).map((s) => `- ${s.school_name}: ${s.change} teachers`),
    "",
    "## Recommendations",
    ...actions.map((a) => `- ${a.title}: ${a.body}`),
    "",
    "## Priority Action Items",
    `- ${buildDrilldownPrimaryAction(topLosses)}`,
    `- ${buildDrilldownReviewNote(topLosses, topGains)}`,
  ].join("\n");
};
